// Schemas describing the shape of conversation flow content (`nodes` + `edges`)
// stored as JSON on `ResourceVersion.content`. Kept separate from `parser.ts`
// so consumers (e.g. the admin flow editor) can import schemas without pulling
// in the tiptap runtime that `parser.ts` initialises at module load.

import { z } from 'zod';
import type { JSONContent } from '@tiptap/core';

export const BlockOptionSchema = z.object({
  id: z.string(),
  title: z.string(),
});

export const BlockReplySchema = z.object({
  id: z.string(),
  content: z.string(),
});

export const TipTapDocSchema = z.looseObject({
  type: z.literal('doc'),
  // Older saved flows can omit `content` when the TipTap doc is empty.
  content: z.array(z.custom<JSONContent>()).optional().default([]),
});

// Fields we read across both the student player and the admin editor.
// `tiptap`, `outgoers` and `label` are missing on a small fraction of saved
// flows so they're modelled as optional - the student parser defaults
// `tiptap` to an empty doc and `outgoers` to `[]`, and admin runs
// `hydrateOutgoers` on load to do the same.
export const NodeDataSchema = z.object({
  tiptap: TipTapDocSchema.optional(),
  outgoers: z.array(z.string()).optional(),
  label: z.string().optional(),
  options: z.array(BlockOptionSchema).optional(),
  pickedOption: z.string().optional(),
  reply: z.string().optional(),
  replied: BlockReplySchema.optional(),
  quizId: z.string().optional(),
});

export const OptionSchema = z.object({
  id: z.string(),
  title: z.string(),
});

export const PersonaSchema = z.object({
  id: z.string(),
  created_by: z.string(),
  avatar_url: z.string().nullable(),
  name: z.string(),
  description: z.string(),
  background_color: z.string(),
  created_at: z.date(),
});

export type BlockOption = z.infer<typeof BlockOptionSchema>;
export type BlockReply = z.infer<typeof BlockReplySchema>;
export type NodeData = z.infer<typeof NodeDataSchema>;
export type Option = z.infer<typeof OptionSchema>;
export type Persona = z.infer<typeof PersonaSchema>;

// -- TipTap node attribute readers ---------------------------------------------
// `JSONContent.attrs` is typed `Record<string, any>`, so reading a known
// attribute off a node yields `any` and silently hides typos or shape drift.
// These readers narrow at the read boundary to the attribute's real type, and
// tolerate the node (or its attrs) being absent so callers can pass an indexed
// child directly without a non-null assertion.

// The persona a chat-bubble block belongs to. Chat bubbles (`dBlock`) and
// media blocks stamp the inheriting persona here; non-bubble nodes omit it.
export const personaIdOf = (node: JSONContent | undefined): string | null => {
  const personaId = node?.attrs?.personaId;
  return typeof personaId === 'string' ? personaId : null;
};

// The quiz question a `dukeQuestion` block renders, used to find the block that
// references a given question when swapping in a freshly published version.
export const questionIdOf = (node: JSONContent | undefined): string | undefined => {
  const questionId = node?.attrs?.questionId;
  return typeof questionId === 'string' ? questionId : undefined;
};
